import React, { useState, FunctionComponent, Dispatch, SetStateAction } from 'react';
import { Bar, Line } from '@reactchartjs/react-chart.js';
import { useQuery } from 'react-query';
import { Typography, Button, Radio, RadioGroup, FormControlLabel, IconButton } from '@material-ui/core';
import { ApiService } from '../../services/api';
import styled from 'styled-components';
import { ApiError } from '../../types/api_error';
import { GraphResponse } from '../../types/dashboard_data';
import { DateRange, FilterList } from '@material-ui/icons';
import { omit, isEqual } from 'lodash';
import { useClientId } from '../Routes/useClientId';
import { LoadingIndicator, DatePickerModal } from '../../components';
import { AdditionalItemPickerModal } from './AdditionalItemsPicker';
import { getGraphContent, getGraphOptions } from './GraphUtils';
import { GraphLabels } from '../../types/graph';

const GraphWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px;
  flex: 1;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
`;

const StyledIconButton = styled(IconButton)`
  display: flex;
  padding: 4px !important;
  border-radius: 0;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  & .MuiTypography-body1 {
    font-size: 12px;
  }
`;

const IconButtonGroup = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`;

interface AdditionalGraphProps {
  mplaceId?: string;
  additional: GraphLabels;
  setAdditional: React.Dispatch<React.SetStateAction<GraphLabels>>;
  isLineChart: string;
  setIsLineChart: (name: string) => void;
  startDate?: string;
  setStartDate: Dispatch<SetStateAction<string | undefined>>;
  endDate?: string;
  setEndDate: Dispatch<SetStateAction<string | undefined>>;
}

export const AdditionalGraphs: FunctionComponent<AdditionalGraphProps> = ({
  mplaceId,
  additional,
  setAdditional,
  isLineChart,
  setIsLineChart,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  const clientId = useClientId();
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [additionalItemsPickerOpen, setAdditionalItemsPickerOpen] = useState<boolean>(false);
  const [additionalLines, setAdditionalLines] = useState<string[]>([]);
  const [graphReady, setGraphReady] = useState(false);

  const {
    data: additionalLinesData,
    isLoading: additionalLinesIsLoading,
    isError: additionalLinesIsError,
    refetch: additionalLinesRefetch,
  } = useQuery<GraphResponse, ApiError>(
    ['invoice-additional-data'],
    ApiService.get(`/client/${clientId}/invoices/additionals-consumption`, {
      startDate,
      endDate,
      mplaceId,
      classCodes: additionalLines,
    }),
    {
      onSuccess: (data) => {
        const mappedAdditional: GraphLabels = {};
        data.labels.forEach((label) => (mappedAdditional[label.toUpperCase()] = true));
        if (!isEqual(Object.keys(omit(additional, ['UKUPNO'])), Object.keys(mappedAdditional)))
          setAdditional({ ...additional, ...mappedAdditional });
        setGraphReady(true);
      },
    }
  );

  const handleModalSubmit = () => {
    setGraphReady(false);
    additionalLinesRefetch();
    setDatePickerOpen(false);
  };

  const handleAdditionalModalSubmit = () => {
    setGraphReady(false);
    additionalLinesRefetch();
    setAdditionalItemsPickerOpen(false);
  };

  const barOpts = getGraphOptions(
    {
      yAxes: [
        {
          stacked: false,
          ticks: {
            beginAtZero: true,
          },
        },
      ],
      xAxes: [
        {
          stacked: false,
        },
      ],
    },
    setAdditional
  );

  const lineOpts = getGraphOptions(
    {
      yAxes: [
        {
          type: 'linear',
          display: true,
          position: 'left',
          id: 'y-axis-1',
        },
      ],
    },
    setAdditional
  );

  const refetchAll = () => {
    additionalLinesRefetch();
  };

  if (additionalLinesIsLoading || !graphReady) {
    return <LoadingIndicator />;
  }

  if (!clientId || additionalLinesIsError || !additionalLinesData) {
    return (
      <InfoWrapper>
        <Typography variant="body2">Nažalost nismo uspjeli dohvatiti podatke.</Typography>
        <Button color="primary" onClick={refetchAll}>
          Pokušaj ponovno
        </Button>
      </InfoWrapper>
    );
  }

  const barGraphContent = getGraphContent(additionalLinesData, additional, true);
  const lineGraphContent = getGraphContent(additionalLinesData, additional, false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLineChart(event.target.value);
  };

  return (
    <GraphWrapper>
      <HeaderRow>
        <StyledRadioGroup aria-label="gender" name="gender1" value={isLineChart} onChange={handleChange}>
          <StyledFormControlLabel value="bar" control={<Radio />} label="Stupčasti prikaz" />
          <StyledFormControlLabel value="line" control={<Radio />} label="Linijski prikaz" />
        </StyledRadioGroup>
        <IconButtonGroup>
          <StyledIconButton onClick={() => setDatePickerOpen(true)}>
            <DateRange color="primary" />
            <Typography variant="caption">Odaberite razdoblje</Typography>
          </StyledIconButton>
          <StyledIconButton onClick={() => setAdditionalItemsPickerOpen(true)}>
            <FilterList color="primary" />
            <Typography variant="caption">Odaberite dodatne stavke</Typography>
          </StyledIconButton>
        </IconButtonGroup>
      </HeaderRow>
      {isLineChart === 'line' ? (
        <Line type="line" data={lineGraphContent} options={lineOpts} />
      ) : (
        <Bar type="bar" data={barGraphContent} options={barOpts} />
      )}
      <DatePickerModal
        open={datePickerOpen}
        handleClose={() => setDatePickerOpen(false)}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        startDate={startDate}
        endDate={endDate}
        handleSubmit={handleModalSubmit}
        title="Odaberite period koji će biti prikazan na grafu"
      />
      <AdditionalItemPickerModal
        open={additionalItemsPickerOpen}
        handleClose={() => setAdditionalItemsPickerOpen(false)}
        handleSubmit={handleAdditionalModalSubmit}
        additionalItems={additionalLinesData?.options || []}
        setChosenItems={setAdditionalLines}
        chosenItems={additionalLines}
      />
    </GraphWrapper>
  );
};
